/**
 * English (en-US) Translations
 * 英文翻译
 */

import type { PricingMessages } from './types';

const enUS: PricingMessages = {
    pageTitle: 'Pricing',

    hero: {
        title: 'Choose the Right Plan for You',
        subtitle: 'Flexible pricing, powerful features',
        billingToggle: {
            monthly: 'Monthly',
            annual: 'Annual',
            saveLabel: 'Save {percent}%',
        },
    },

    plans: {
        lite: {
            name: 'Lite',
            description: 'Perfect for trying out all features once per month',
            cta: 'Buy Now',
            priceLabel: '{amount} / month',
        },
        pro: {
            name: 'Pro',
            description: 'Unlimited drafts for professional patent practitioners',
            cta: 'Buy Now',
            priceLabel: '{amount} / month',
            features: {
                unlimited_drafts: 'Pro document allowance',
                priority_support: 'Priority support',
            },
        },
        max: {
            name: 'Max',
            description: 'Unlimited document creation for high-volume patent drafting',
            cta: 'Buy Now',
            priceLabel: '{amount} / month',
            features: {
                unlimited_documents: 'Unlimited document creation',
            },
        },
        enterprise: {
            name: 'Enterprise',
            description: 'Custom solutions for teams and organizations',
            cta: 'Contact Us',
            priceLabel: 'Custom Pricing',
        },
    },

    features: {
        drafting: {
            title: 'Patent Drafting',
            items: {
                specification_generation: 'Specification Generation',
                claims_generation: 'Claims Generation',
                abstract_generation: 'Abstract Generation',
                embodiment_expansion: 'Embodiment Expansion',
                figure_handling: 'Figure Handling',
                drafts_per_month: 'Drafts per Month',
            },
        },
        export: {
            title: 'Export & Format',
            items: {
                export_word: 'Word Export',
                export_pdf: 'PDF Export',
                multi_jurisdiction: 'Multi-Jurisdiction Formats',
                custom_templates: 'Custom Templates',
            },
        },
        collaboration: {
            title: 'Collaboration',
            items: {
                workspace_share: 'Share',
                workspace_comment: 'Comment',
                workspace_edit: 'Collaborative Editing',
                version_history: 'Version History',
            },
        },
        administration: {
            title: 'Administration',
            items: {
                admin_backend: 'Admin Backend',
                user_management: 'User Management',
                usage_analytics: 'Usage Analytics',
            },
        },
        security: {
            title: 'Security',
            items: {
                data_encryption: 'Data Encryption',
                sso: 'Single Sign-On (SSO)',
                audit_logs: 'Audit Logs',
            },
        },
        support: {
            title: 'Support',
            items: {
                support_model: 'Support Model',
                training: 'Training & Onboarding',
            },
        },
        values: {
            unlimited: 'Unlimited',
            included: 'Included',
            notIncluded: 'Not Included',
            custom: 'Custom',
            allJurisdictions: 'All Patent Offices',
        },
    },

    faq: {
        title: 'Frequently Asked Questions',
        items: [
            {
                question: 'What is the quality of generated patents?',
                answer:
          'Our AI system is trained on a vast amount of high-quality patent data and can generate professional-grade patent documents. All content is carefully designed to ensure clear logic and accurate expression.',
            },
            {
                question: 'Which patent office formats are supported?',
                answer:
          'We support major patent office formats including China National Intellectual Property Administration (CNIPA), United States Patent and Trademark Office (USPTO), European Patent Office (EPO), Japan Patent Office (JPO), and many other jurisdictions.',
            },
            {
                question: 'How is data security ensured?',
                answer:
          'We use bank-level encryption technology to protect your data. All data transmission is encrypted with SSL. We strictly comply with data protection regulations and will not share your patent content with third parties.',
            },
            {
                question: 'Do I need a technical background?',
                answer:
          'No. Our system is designed to be simple and intuitive, making it easy for anyone to get started. For professional users, we also provide advanced features and customization options.',
            },
            {
                question: 'Can it be customized?',
                answer:
          'Yes. Enterprise plan provides complete customization services, including dedicated templates, workflow customization, API integration, etc. Our team will provide solutions based on your specific needs.',
            },
            {
                question: 'How is pricing calculated?',
                answer:
          'Lite plan is billed at $9.9 per month with 1 draft per month. Pro plan offers both monthly and annual payment options, with a 20% discount for annual payment. Enterprise pricing is customized based on team size and requirements.',
            },
            {
                question: 'Does it support team collaboration?',
                answer:
          'Both Pro and Enterprise plans support team collaboration features, including document sharing, comments, version history, etc. Enterprise plan also provides advanced collaborative editing and user management functions.',
            },
            {
                question: 'Can I get a refund if I\'m not satisfied?',
                answer:
          'We offer a 7-day money-back guarantee. If you are not satisfied with the service, you can apply for a full refund within 7 days of purchase.',
            },
        ],
    },

    cta: {
        title: 'Ready to Get Started?',
        subtitle: 'Choose the plan that\'s right for you and start your patent drafting journey today',
        buttonText: 'Get Started',
        selectPlan: 'Select Plan',
        contactSales: 'Contact Sales',
        learnMore: 'Learn More',
        getStarted: 'Get Started',
        emailSubject: 'Eureka Drafting - Enterprise Inquiry',
        planSelected: '{plan} plan selected',
        getStartedMessage: 'Redirecting to registration page...',
    },

    errors: {
        loadPlansFailed: 'Failed to load pricing information, please try again later',
        invalidPlan: 'Invalid pricing plan',
        networkError: 'Network connection failed',
    },

    common: {
        perMonth: '/ month',
        perYear: '/ year',
        billedMonthly: 'Billed monthly',
        billedAnnually: 'Billed annually',
        popular: 'Popular',
        recommended: 'Recommended',
        comingSoon: 'Coming Soon',
    },

    payment: {
        verifying: 'Verifying payment...',
        pleaseWait: 'Please wait while we confirm your payment',
        backToPricing: 'Back to Pricing',
        tryAgain: 'Try Again',

        success: {
            title: 'Payment Successful!',
            message: 'Thank you for subscribing. Your account has been activated.',
        },

        failed: {
            title: 'Payment Failed',
            message: 'We were unable to process your payment. Please try again.',
        },

        canceled: {
            title: 'Payment Canceled',
            message: 'You canceled the payment. No charges were made.',
        },

        errors: {
            createSessionFailed: 'Failed to create payment session. Please try again.',
            verifyFailed: 'Failed to verify payment status',
            verificationFailed: 'Payment verification failed',
            verificationError: 'An error occurred during verification',
            invalidCallback: 'Invalid payment callback',
            noPriceId: 'Price information not configured',
        },
    },
};

export default enUS;
